import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth, ProtectedRoute } from "./auth";
import { Home as HomeIcon, Palette, Sparkles, Layers, Receipt, ShoppingBag, ClipboardList, LogOut, Plus, Trash2, Mail, Loader2, Pencil, X, ArrowRight, ShieldCheck, Brush, UserPlus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Portal Shell (sidebar + outlet)
// ---------------------------------------------------------------------------
const NAV = [
  { to: "/portal", label: "Overview", icon: Sparkles, end: true },
  { to: "/portal/homes", label: "My Homes", icon: HomeIcon },
  { to: "/portal/paint-history", label: "Paint History", icon: Brush },
  { to: "/portal/quiz-results", label: "Quiz Results", icon: ClipboardList },
  { to: "/portal/saved-colors", label: "Saved Colors", icon: Palette },
  { to: "/portal/saved-palettes", label: "Saved Palettes", icon: Layers },
  { to: "/portal/estimates", label: "Estimates", icon: Receipt },
  { to: "/portal/purchases", label: "Purchases", icon: ShoppingBag },
  { to: "/portal/referrals", label: "My Referrals", icon: UserPlus },
  { to: "/portal/account", label: "Account", icon: ShieldCheck },
];

export function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <ProtectedRoute>
      <div className="min-h-screen pt-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-[240px_1fr] gap-10">
          <aside className="lg:sticky lg:top-28 self-start" data-testid="portal-sidebar">
            <div className="mb-8">
              <p className="eyebrow text-gold">Client Portal</p>
              <p className="font-serif text-2xl leading-tight text-ink mt-1">{(user && user.name) || "Welcome"}</p>
              <p className="text-xs text-ink/60 break-all mt-1">{user && user.email}</p>
              {user && !user.email_verified && user.auth_provider !== "google" && (
                <div className="mt-3 text-[11px] bg-gold/10 text-ink border-l-2 border-gold px-3 py-2 leading-relaxed" data-testid="verify-nudge">
                  Verify your email to unlock ordering & downloads.
                </div>
              )}
            </div>
            <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-3 lg:pb-0">
              {NAV.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={`portal-link-${label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) => `flex items-center gap-3 whitespace-nowrap px-3 py-2.5 text-sm border-l-2 transition-colors ${isActive ? "border-gold text-ink bg-white" : "border-transparent text-ink/70 hover:text-ink hover:bg-white/60"}`}
                >
                  <Icon size={15} className="shrink-0" /> {label}
                </NavLink>
              ))}
              <button
                data-testid="portal-logout"
                onClick={async () => { await logout(); toast.success("Signed out"); navigate("/"); }}
                className="flex items-center gap-3 whitespace-nowrap px-3 py-2.5 text-sm border-l-2 border-transparent text-ink/60 hover:text-red-600 hover:bg-white/60"
              >
                <LogOut size={15} /> Sign out
              </button>
            </nav>
          </aside>
          <main className="min-w-0" data-testid="portal-main">
            <Outlet />
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
export function PortalOverview() {
  const { http } = useAuth();
  const [summary, setSummary] = useState(null);
  useEffect(() => { http.get("/portal/me").then((r) => setSummary(r.data)).catch(() => {}); }, [http]);
  const counts = summary?.counts || {};
  const tiles = [
    { key: "homes", label: "Homes", to: "/portal/homes" },
    { key: "paint_history", label: "Paint History", to: "/portal/paint-history" },
    { key: "saved_colors", label: "Saved Colors", to: "/portal/saved-colors" },
    { key: "saved_palettes", label: "Palettes", to: "/portal/saved-palettes" },
    { key: "quiz_results", label: "Quiz Results", to: "/portal/quiz-results" },
    { key: "estimates", label: "Estimates", to: "/portal/estimates" },
    { key: "orders", label: "Purchases", to: "/portal/purchases" },
    { key: "referrals", label: "Referrals", to: "/portal/referrals" },
  ];
  return (
    <div className="space-y-10">
      <div>
        <p className="eyebrow text-gold">Welcome back</p>
        <h1 className="font-serif text-4xl md:text-5xl mt-2 text-ink">Your Design Portal</h1>
        <p className="mt-4 text-ink/70 max-w-2xl">Everything you've explored with Beverly Hills Paint Co., organized by home. Save colors, retake quizzes, request estimates and revisit your purchases in one place.</p>
      </div>
      <section>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="portal-overview-tiles">
          {tiles.map((t) => (
            <Link key={t.key} to={t.to} className="border border-black/10 bg-white p-5 hover:border-gold transition-colors group">
              <p className="overline text-[10px] text-ink/50">{t.label}</p>
              <p className="font-serif text-4xl mt-1 text-ink">{counts[t.key] ?? "-"}</p>
              <p className="mt-2 text-xs text-gold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">Open <ArrowRight size={12}/></p>
            </Link>
          ))}
        </div>
      </section>
      <section className="grid md:grid-cols-3 gap-4">
        <CTA to="/color-quiz" title="Take the Color Quiz" caption="14 questions, 3 curated color picks" />
        <CTA to="/sheen-quiz" title="Find your ideal sheen" caption="Match finish to the room's job" />
        <CTA to="/contact" title="Request an estimate" caption="A designer replies within 24 hours" gold />
      </section>
    </div>
  );
}

function CTA({ to, title, caption, gold }) {
  return (
    <Link to={to} className={`block p-6 ${gold ? "bg-ink text-white" : "bg-white border border-black/10"} hover:bg-gold hover:text-white transition-colors`}>
      <p className="font-serif text-2xl leading-tight">{title}</p>
      <p className={`mt-2 text-xs ${gold ? "text-white/70" : "text-ink/60"}`}>{caption}</p>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Homes
// ---------------------------------------------------------------------------
export function PortalHomes() {
  const { http } = useAuth();
  const [homes, setHomes] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", city: "", notes: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = async () => setHomes((await http.get("/portal/homes")).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) {
        await http.patch(`/portal/homes/${editing.id}`, form);
        toast.success("Home updated");
      } else {
        await http.post("/portal/homes", form);
        toast.success("Home added");
      }
      setForm({ name: "", address: "", city: "", notes: "" });
      setEditing(null); setShowForm(false);
      await load();
    } catch (e2) { toast.error("Could not save home"); }
    finally { setSaving(false); }
  };
  const startEdit = (h) => {
    setEditing(h); setForm({ name: h.name || "", address: h.address || "", city: h.city || "", notes: h.notes || "" });
    setShowForm(true);
  };
  const remove = async (h) => {
    if (!window.confirm(`Remove "${h.name}"? Saved colors under this home will remain.`)) return;
    await http.delete(`/portal/homes/${h.id}`);
    toast.success("Home removed"); await load();
  };

  return (
    <div className="space-y-8">
      <Header eyebrow="My Homes" title="Organize colors, quizzes and paint history by property." action={
        <Button onClick={() => { setEditing(null); setForm({ name: "", address: "", city: "", notes: "" }); setShowForm(true); }} data-testid="add-home-btn" className="rounded-none bg-ink text-white hover:bg-gold px-6 py-5 overline">
          <Plus size={14} className="mr-2" /> Add a home
        </Button>
      } />
      <AnimatePresence>
        {showForm && (
          <motion.form initial={{opacity:0,y:-6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} onSubmit={submit} className="bg-white border border-black/10 p-6 space-y-4" data-testid="home-form">
            <div className="flex items-center justify-between">
              <p className="font-serif text-xl">{editing ? "Edit home" : "Add a home"}</p>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}><X size={16}/></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Nickname" required value={form.name} onChange={(v)=>setForm({...form,name:v})} testid="home-name" />
              <Input label="City" value={form.city} onChange={(v)=>setForm({...form,city:v})} testid="home-city" />
              <div className="md:col-span-2"><Input label="Address" value={form.address} onChange={(v)=>setForm({...form,address:v})} testid="home-address" /></div>
              <div className="md:col-span-2">
                <p className="overline text-[10px] text-ink/60 mb-1">Notes</p>
                <textarea rows={3} value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none" data-testid="home-notes" />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="home-submit" className="rounded-none bg-gold text-white hover:bg-gold-dark px-8 py-5 overline">{saving ? "Saving…" : (editing ? "Save changes" : "Add home")}</Button>
          </motion.form>
        )}
      </AnimatePresence>
      {homes === null ? <SkeletonBlock /> : homes.length === 0 ? (
        <Empty label="No homes yet." helper="Add the properties you're planning to paint to organize your saved colors, quiz results and paint history." />
      ) : (
        <div className="grid md:grid-cols-2 gap-5" data-testid="homes-list">
          {homes.map((h) => (
            <div key={h.id} className="bg-white border border-black/10 p-5" data-testid={`home-card-${h.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-serif text-xl">{h.name}</p>
                  <p className="text-xs text-ink/60 mt-1">{[h.address, h.city].filter(Boolean).join(", ") || "Location not set"}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => startEdit(h)} className="text-ink/60 hover:text-gold" data-testid={`edit-home-${h.id}`}><Pencil size={16}/></button>
                  <button onClick={() => remove(h)} className="text-ink/60 hover:text-red-600" data-testid={`delete-home-${h.id}`}><Trash2 size={16}/></button>
                </div>
              </div>
              {h.notes && <p className="mt-3 text-sm text-ink/70">{h.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quiz Results
// ---------------------------------------------------------------------------
export function PortalQuizResults() {
  const { http } = useAuth();
  const [items, setItems] = useState(null);
  const load = async () => setItems((await http.get("/portal/quiz-results")).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const remove = async (q) => {
    if (!window.confirm("Delete this saved quiz result?")) return;
    await http.delete(`/portal/quiz-results/${q.id}`); toast.success("Removed"); await load();
  };
  return (
    <div className="space-y-8">
      <Header eyebrow="Quiz Results" title="Every quiz you've taken with us, saved for later." />
      {items === null ? <SkeletonBlock /> : items.length === 0 ? (
        <Empty label="No saved quiz results." helper="Take the Color Quiz or Sheen Quiz and press Save to add them here." action={<Link to="/color-quiz" className="text-gold underline underline-offset-4">Start the Color Quiz</Link>} />
      ) : (
        <div className="space-y-4" data-testid="quiz-results-list">
          {items.map((q) => (
            <div key={q.id} className="bg-white border border-black/10 p-5 flex items-start justify-between gap-4" data-testid={`quiz-result-${q.id}`}>
              <div className="min-w-0">
                <p className="overline text-[10px] text-ink/50">{q.kind === "sheen" ? "Sheen quiz" : "Color quiz"}</p>
                <p className="font-serif text-xl mt-1">{q.title || (q.kind === "sheen" ? "Sheen recommendation" : "Color palette")}</p>
                <p className="text-xs text-ink/50 mt-1">{new Date(q.created_at).toLocaleDateString()}</p>
                {q.kind === "color" && (
                  <div className="flex gap-3 mt-3">
                    {["best_match","safe_pick","designer_pick"].map((k) => q.results?.[k] && (
                      <div key={k} className="flex items-center gap-2">
                        <div className="w-8 h-8 border border-black/10" style={{ background: q.results[k].hex }} />
                        <div className="text-xs">
                          <p>{q.results[k].name}</p>
                          <p className="text-ink/50">{q.results[k].brand} {q.results[k].code}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {q.kind === "sheen" && q.results?.recommended && (
                  <p className="mt-3 text-sm">Recommended: <span className="text-gold">{q.results.recommended.sheen}</span></p>
                )}
              </div>
              <button onClick={() => remove(q)} className="text-ink/50 hover:text-red-600 shrink-0" data-testid={`delete-quiz-${q.id}`}><Trash2 size={15}/></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved Colors
// ---------------------------------------------------------------------------
export function PortalSavedColors() {
  const { http } = useAuth();
  const [items, setItems] = useState(null);
  const load = async () => setItems((await http.get("/portal/saved-colors")).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const remove = async (c) => { await http.delete(`/portal/saved-colors/${c.id}`); toast.success("Removed"); await load(); };
  return (
    <div className="space-y-8">
      <Header eyebrow="Saved Colors" title="Your color library." />
      {items === null ? <SkeletonBlock /> : items.length === 0 ? (
        <Empty label="No saved colors yet." helper="Save any color while browsing the Color Library, Color Quiz results or the Sheen Guide." action={<Link to="/colors" className="text-gold underline underline-offset-4">Browse the Color Library</Link>} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="saved-colors-list">
          {items.map((c) => (
            <div key={c.id} className="bg-white border border-black/10 overflow-hidden" data-testid={`saved-color-${c.id}`}>
              <div className="aspect-[5/3]" style={{ background: c.hex }} />
              <div className="p-4 flex items-start justify-between gap-3">
                <div>
                  <p className="font-serif text-lg leading-tight">{c.name}</p>
                  <p className="text-xs text-ink/60">{c.brand} · {c.code}</p>
                </div>
                <button onClick={() => remove(c)} className="text-ink/50 hover:text-red-600" data-testid={`delete-color-${c.id}`}><Trash2 size={15}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Saved Palettes
// ---------------------------------------------------------------------------
export function PortalSavedPalettes() {
  const { http } = useAuth();
  const [items, setItems] = useState(null);
  const load = async () => setItems((await http.get("/portal/saved-palettes")).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const remove = async (p) => { await http.delete(`/portal/saved-palettes/${p.id}`); toast.success("Removed"); await load(); };
  return (
    <div className="space-y-8">
      <Header eyebrow="Saved Palettes" title="Multi-color stories you've saved." />
      {items === null ? <SkeletonBlock /> : items.length === 0 ? (
        <Empty label="No saved palettes yet." helper="Purchase a designer-curated palette or save one from your color quiz results to see it here." action={<Link to="/palettes" className="text-gold underline underline-offset-4">Explore designer palettes</Link>} />
      ) : (
        <div className="space-y-4" data-testid="saved-palettes-list">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-black/10 p-5" data-testid={`saved-palette-${p.id}`}>
              <div className="flex items-start justify-between">
                <p className="font-serif text-xl">{p.name}</p>
                <button onClick={() => remove(p)} className="text-ink/50 hover:text-red-600" data-testid={`delete-palette-${p.id}`}><Trash2 size={15}/></button>
              </div>
              <div className="flex mt-4 h-16 border border-black/10">
                {(p.colors || []).map((c, i) => (
                  <div key={i} title={`${c.name || ''}`} className="flex-1" style={{ background: c.hex || "#eee" }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estimates
// ---------------------------------------------------------------------------
export function PortalEstimates() {
  const { http, user } = useAuth();
  const [items, setItems] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: (user && user.name) || "", email: (user && user.email) || "", phone: "", project_type: "Interior repaint", service_area: "", scope: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => setItems((await http.get("/portal/estimates")).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await http.post("/portal/estimates/request", form);
      toast.success("Estimate requested. We'll be in touch within 24 hours.");
      setShowForm(false); await load();
    } catch (e2) { toast.error("Could not submit estimate."); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8">
      <Header eyebrow="Estimates" title="Track your requests and responses." action={
        <Button onClick={() => setShowForm(true)} data-testid="request-estimate-btn" className="rounded-none bg-gold text-white hover:bg-gold-dark px-6 py-5 overline"><Plus size={14} className="mr-2"/> Request estimate</Button>
      } />
      <AnimatePresence>
        {showForm && (
          <motion.form initial={{opacity:0,y:-6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} onSubmit={submit} className="bg-white border border-black/10 p-6 space-y-4" data-testid="estimate-form">
            <div className="flex items-center justify-between">
              <p className="font-serif text-xl">New estimate request</p>
              <button type="button" onClick={() => setShowForm(false)}><X size={16}/></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Input label="Name" required value={form.name} onChange={(v)=>setForm({...form,name:v})} testid="est-name" />
              <Input label="Email" required type="email" value={form.email} onChange={(v)=>setForm({...form,email:v})} testid="est-email" />
              <Input label="Phone" value={form.phone} onChange={(v)=>setForm({...form,phone:v})} testid="est-phone" />
              <Input label="Project type" value={form.project_type} onChange={(v)=>setForm({...form,project_type:v})} testid="est-project" />
              <Input label="Service area" value={form.service_area} onChange={(v)=>setForm({...form,service_area:v})} testid="est-area" />
              <div className="md:col-span-2">
                <p className="overline text-[10px] text-ink/60 mb-1">Scope / details</p>
                <textarea rows={4} value={form.scope} onChange={(e)=>setForm({...form,scope:e.target.value})} className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none" data-testid="est-scope" />
              </div>
            </div>
            <Button type="submit" disabled={saving} data-testid="est-submit" className="rounded-none bg-ink text-white hover:bg-gold px-8 py-5 overline">{saving ? "Sending…" : "Submit request"}</Button>
          </motion.form>
        )}
      </AnimatePresence>
      {items === null ? <SkeletonBlock /> : items.length === 0 ? (
        <Empty label="No estimates yet." helper="Request an estimate above and our team will reply within 24 hours." />
      ) : (
        <div className="space-y-3" data-testid="estimates-list">
          {items.map((e) => (
            <div key={e.id} className="bg-white border border-black/10 p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`estimate-${e.id}`}>
              <div>
                <p className="font-serif text-lg">{e.project_type || "Project estimate"}</p>
                <p className="text-xs text-ink/60">{new Date(e.created_at).toLocaleDateString()} · {e.service_area || "Los Angeles"}</p>
                {e.scope && <p className="text-sm text-ink/70 mt-1 line-clamp-2">{e.scope}</p>}
              </div>
              <span className="overline text-[10px] px-3 py-1 bg-gold/10 text-gold border border-gold/40">{e.status || "requested"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Purchases
// ---------------------------------------------------------------------------
export function PortalPurchases() {
  const { http, user } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => { http.get("/portal/purchases").then((r) => setItems(r.data)).catch(() => setItems([])); }, [http]);
  const canDownload = user?.email_verified;
  return (
    <div className="space-y-8">
      <Header eyebrow="Purchases" title="Your Beverly Hills Paint Co. order history." />
      {!canDownload && user?.auth_provider === "email" && (
        <div className="text-xs bg-gold/10 border-l-2 border-gold px-4 py-3 text-ink/80" data-testid="verify-required-notice">
          Digital downloads unlock once your email is verified.
        </div>
      )}
      {items === null ? <SkeletonBlock /> : items.length === 0 ? (
        <Empty label="No purchases yet." helper="Explore Paint Courses, Renderings and Designer Palettes." action={<Link to="/courses" className="text-gold underline underline-offset-4">Browse courses</Link>} />
      ) : (
        <div className="space-y-3" data-testid="purchases-list">
          {items.map((o) => (
            <div key={o.id} className="bg-white border border-black/10 p-4" data-testid={`purchase-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-serif text-lg">Order · {new Date(o.created_at).toLocaleDateString()}</p>
                  <p className="text-xs text-ink/60">{(o.items || []).map((it) => it.name).join(" · ")}</p>
                </div>
                <p className="font-serif text-2xl">${Number(o.total || 0).toFixed(2)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account
// ---------------------------------------------------------------------------
export function PortalAccount() {
  const { user, http, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [marketing, setMarketing] = useState(!!user?.marketing_consent);
  const [password, setPassword] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setName(user?.name || ""); setPhone(user?.phone || ""); setMarketing(!!user?.marketing_consent); }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await http.patch("/auth/profile", { name, phone });
      await http.patch("/auth/marketing-consent", { marketing_consent: marketing });
      await refresh(); toast.success("Profile updated");
    } catch (e2) { toast.error("Could not update profile"); }
    finally { setSaving(false); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    try { await http.post("/auth/change-password", password); toast.success("Password changed"); setPassword({ current_password: "", new_password: "" }); }
    catch (e2) { toast.error(e2.response?.data?.detail || "Could not change password"); }
  };
  const resendVerify = async () => {
    try { await http.post("/auth/verify-email/request"); toast.success("Verification email sent (if configured)"); }
    catch (e) { toast.error("Could not send verification email"); }
  };
  return (
    <div className="space-y-10">
      <Header eyebrow="Account" title="Your profile & preferences." />
      <form onSubmit={saveProfile} className="bg-white border border-black/10 p-6 space-y-4 max-w-2xl" data-testid="account-profile-form">
        <div className="grid md:grid-cols-2 gap-4">
          <Input label="Name" value={name} onChange={setName} testid="account-name" />
          <Input label="Phone" value={phone} onChange={setPhone} testid="account-phone" />
        </div>
        <label className="flex items-start gap-2 text-xs text-ink/70">
          <input type="checkbox" checked={marketing} onChange={(e)=>setMarketing(e.target.checked)} className="mt-1" data-testid="account-marketing" />
          <span>Receive color inspiration & updates.</span>
        </label>
        <Button type="submit" disabled={saving} className="rounded-none bg-ink text-white hover:bg-gold px-6 py-4 overline" data-testid="account-save">{saving ? "Saving…" : "Save profile"}</Button>
      </form>
      {user?.auth_provider !== "google" && (
        <form onSubmit={changePw} className="bg-white border border-black/10 p-6 space-y-4 max-w-2xl" data-testid="account-password-form">
          <p className="font-serif text-xl">Change password</p>
          {!user?.email_verified && <p className="text-xs bg-gold/10 border-l-2 border-gold px-3 py-2 text-ink/80">Verify your email first — <button type="button" onClick={resendVerify} className="text-gold underline" data-testid="resend-verify-btn">resend link</button></p>}
          <Input label="Current password" type="password" required value={password.current_password} onChange={(v)=>setPassword({...password, current_password:v})} testid="account-current-pw" />
          <Input label="New password" type="password" required minLength={8} value={password.new_password} onChange={(v)=>setPassword({...password, new_password:v})} testid="account-new-pw" />
          <Button type="submit" disabled={!user?.email_verified} className="rounded-none bg-gold text-white hover:bg-gold-dark px-6 py-4 overline" data-testid="account-change-pw">Update password</Button>
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paint History — per-room paint record with optional photos
// ---------------------------------------------------------------------------
const MAX_PHOTO_BYTES = 2_500_000; // 2.5 MB cap before base64 encoding
const REFERRAL_STATUS_LABEL = {
  submitted: "Submitted",
  contacted: "We've reached out",
  consult_booked: "Consult booked",
  job_booked: "Job booked",
  paid_out: "Reward paid",
  closed: "Closed",
};

async function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PortalPaintHistory() {
  const { http } = useAuth();
  const [entries, setEntries] = useState(null);
  const [homes, setHomes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { home_id: "", room_name: "", manufacturer: "Benjamin Moore", color_code: "", color_name: "", sheen: "", hex: "", notes: "", before_photo: "", after_photo: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [h, e] = await Promise.all([http.get("/portal/homes"), http.get("/portal/paint-history")]);
    setHomes(h.data); setEntries(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...empty, home_id: homes[0]?.id || "" });
    setShowForm(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ ...empty, ...row });
    setShowForm(true);
  };
  const handlePhoto = async (field, file) => {
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo is too big. Please pick one under 2.5 MB.");
      return;
    }
    const b64 = await readFileAsBase64(file);
    setForm((f) => ({ ...f, [field]: b64 }));
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.home_id) { toast.error("Please pick a home."); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      // Server-side model doesn't accept empty strings for optional dates — normalise.
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (editing) {
        await http.patch(`/portal/paint-history/${editing.id}`, payload);
        toast.success("Paint record updated");
      } else {
        await http.post("/portal/paint-history", payload);
        toast.success("Paint record saved");
      }
      setShowForm(false); setEditing(null); setForm(empty);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Try again.");
    } finally { setSaving(false); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete the record for ${row.room_name}?`)) return;
    await http.delete(`/portal/paint-history/${row.id}`);
    toast.success("Deleted"); await load();
  };

  const grouped = useMemo(() => {
    if (!entries) return {};
    const homesById = Object.fromEntries(homes.map((h) => [h.id, h]));
    return entries.reduce((acc, e) => {
      const key = e.home_id || "unassigned";
      if (!acc[key]) acc[key] = { home: homesById[key], entries: [] };
      acc[key].entries.push(e);
      return acc;
    }, {});
  }, [entries, homes]);

  return (
    <div className="space-y-8">
      <Header eyebrow="Paint History" title="Every room, every color, always at your fingertips." action={
        <Button
          onClick={openAdd}
          disabled={homes.length === 0}
          data-testid="add-paint-history-btn"
          className="rounded-none bg-ink text-white hover:bg-gold px-6 py-5 overline"
        >
          <Plus size={14} className="mr-2" /> Log a room
        </Button>
      } />
      <p className="text-sm text-ink/60 max-w-2xl -mt-4">
        A private record of the exact paint codes, palettes and sheens used in every room we finish for you.
        Add before &amp; after photos if you'd like — it's completely optional.
      </p>
      {homes.length === 0 && (
        <div className="bg-gold/10 border-l-2 border-gold p-5 text-sm">
          Add a home first from <Link to="/portal/homes" className="text-gold underline">My Homes</Link> so we can group your paint records by property.
        </div>
      )}
      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{opacity:0,y:-6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}}
            onSubmit={submit}
            className="bg-white border border-black/10 p-6 space-y-5"
            data-testid="paint-history-form"
          >
            <div className="flex items-center justify-between">
              <p className="font-serif text-xl">{editing ? "Edit paint record" : "Log a room's paint"}</p>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }}><X size={16}/></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <p className="overline text-[10px] text-ink/60 mb-1">Home *</p>
                <select required value={form.home_id} onChange={(e)=>setForm({...form, home_id: e.target.value})} data-testid="ph-home" className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none">
                  <option value="">Select a home…</option>
                  {homes.map((h)=> <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </label>
              <Input label="Room" required value={form.room_name} onChange={(v)=>setForm({...form,room_name:v})} testid="ph-room" />
              <label className="block">
                <p className="overline text-[10px] text-ink/60 mb-1">Brand *</p>
                <select required value={form.manufacturer} onChange={(e)=>setForm({...form, manufacturer: e.target.value})} data-testid="ph-brand" className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none">
                  <option>Benjamin Moore</option>
                  <option>Sherwin-Williams</option>
                  <option>Farrow &amp; Ball</option>
                  <option>Other</option>
                </select>
              </label>
              <Input label="Color code" required value={form.color_code} onChange={(v)=>setForm({...form,color_code:v})} testid="ph-code" />
              <Input label="Color name" value={form.color_name} onChange={(v)=>setForm({...form,color_name:v})} testid="ph-name" />
              <label className="block">
                <p className="overline text-[10px] text-ink/60 mb-1">Sheen</p>
                <select value={form.sheen || ""} onChange={(e)=>setForm({...form, sheen: e.target.value})} data-testid="ph-sheen" className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none">
                  <option value="">—</option>
                  <option>Flat / Matte</option>
                  <option>Eggshell</option>
                  <option>Satin</option>
                  <option>Semi-Gloss</option>
                  <option>High Gloss</option>
                </select>
              </label>
              <label className="block">
                <p className="overline text-[10px] text-ink/60 mb-1">Hex (optional)</p>
                <div className="flex items-center gap-2">
                  <input type="text" placeholder="#EDE7DC" value={form.hex || ""} onChange={(e)=>setForm({...form,hex:e.target.value})} data-testid="ph-hex" className="flex-1 border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none" />
                  {form.hex && <span className="w-8 h-8 border border-black/15" style={{background: form.hex}} />}
                </div>
              </label>
              <Input label="Painted on" value={form.painted_on || ""} onChange={(v)=>setForm({...form,painted_on:v})} testid="ph-date" />
              <div className="md:col-span-2">
                <p className="overline text-[10px] text-ink/60 mb-1">Notes</p>
                <textarea rows={3} value={form.notes || ""} onChange={(e)=>setForm({...form,notes:e.target.value})} data-testid="ph-notes" className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <PhotoField label="Before photo (optional)" value={form.before_photo} onFile={(f)=>handlePhoto("before_photo", f)} onClear={()=>setForm({...form, before_photo:""})} testid="ph-before" />
              <PhotoField label="After photo (optional)" value={form.after_photo} onFile={(f)=>handlePhoto("after_photo", f)} onClear={()=>setForm({...form, after_photo:""})} testid="ph-after" />
            </div>
            <Button type="submit" disabled={saving} data-testid="paint-history-submit" className="rounded-none bg-gold text-white hover:bg-gold-dark px-8 py-5 overline">
              {saving ? "Saving…" : (editing ? "Save changes" : "Save record")}
            </Button>
          </motion.form>
        )}
      </AnimatePresence>
      {entries === null ? <SkeletonBlock /> : Object.keys(grouped).length === 0 ? (
        <Empty
          label="No paint records yet."
          helper="Log the paint you're using in each room so you never lose the code, sheen or palette."
          action={homes.length > 0 && <button onClick={openAdd} className="text-gold underline underline-offset-4">Log your first room</button>}
        />
      ) : (
        <div className="space-y-10" data-testid="paint-history-list">
          {Object.entries(grouped).map(([hid, group]) => (
            <div key={hid}>
              <p className="overline text-[10px] text-ink/60 mb-4">{group.home?.name || "Unassigned"}</p>
              <div className="grid md:grid-cols-2 gap-4">
                {group.entries.map((row) => (
                  <div key={row.id} className="bg-white border border-black/10 overflow-hidden" data-testid={`paint-history-${row.id}`}>
                    <div className="grid grid-cols-[80px_1fr]">
                      <div className="h-full" style={{ background: row.hex || "#efefef" }} />
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-serif text-lg leading-tight truncate">{row.room_name}</p>
                            <p className="text-xs text-ink/60 mt-0.5">{row.manufacturer} · {row.color_code}{row.color_name ? ` · ${row.color_name}` : ""}</p>
                            {row.sheen && <p className="text-[11px] text-ink/50 mt-0.5">{row.sheen}</p>}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button onClick={() => openEdit(row)} className="text-ink/60 hover:text-gold p-1" data-testid={`edit-ph-${row.id}`}><Pencil size={14}/></button>
                            <button onClick={() => remove(row)} className="text-ink/60 hover:text-red-600 p-1" data-testid={`delete-ph-${row.id}`}><Trash2 size={14}/></button>
                          </div>
                        </div>
                        {row.notes && <p className="text-xs text-ink/70 mt-2 line-clamp-2">{row.notes}</p>}
                        {(row.before_photo || row.after_photo) && (
                          <div className="flex gap-2 mt-3">
                            {row.before_photo && <img src={row.before_photo} alt="Before" className="h-16 w-16 object-cover border border-black/10" data-testid={`ph-before-thumb-${row.id}`} />}
                            {row.after_photo && <img src={row.after_photo} alt="After" className="h-16 w-16 object-cover border border-black/10" data-testid={`ph-after-thumb-${row.id}`} />}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhotoField({ label, value, onFile, onClear, testid }) {
  return (
    <div>
      <p className="overline text-[10px] text-ink/60 mb-2">{label}</p>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="" className="h-32 w-32 object-cover border border-black/15" />
          <button type="button" onClick={onClear} className="absolute -top-2 -right-2 bg-white border border-black/20 rounded-full p-1 shadow-sm hover:bg-red-50" data-testid={`${testid}-clear`}><X size={12}/></button>
        </div>
      ) : (
        <label className="flex items-center justify-center gap-2 border border-dashed border-black/25 px-4 py-6 text-xs text-ink/60 hover:border-gold hover:text-gold transition-colors cursor-pointer" data-testid={`${testid}-upload`}>
          <Upload size={14} /> Choose a photo (max 2.5 MB)
          <input type="file" accept="image/*" onChange={(e)=>onFile(e.target.files?.[0])} className="hidden" />
        </label>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// My Referrals — logged-in view of everyone the user has referred
// ---------------------------------------------------------------------------
export function PortalReferrals() {
  const { http } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => { http.get("/portal/referrals").then((r) => setItems(r.data)).catch(() => setItems([])); }, [http]);
  return (
    <div className="space-y-8">
      <Header eyebrow="My Referrals" title="Every friend you've sent our way." action={
        <Link to="/refer" data-testid="portal-refer-btn" className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3 overline hover:bg-gold transition-colors">
          <UserPlus size={14}/> Refer someone new
        </Link>
      } />
      <div className="bg-white border border-black/10 p-6">
        <p className="font-serif text-xl">How the reward works</p>
        <ul className="mt-3 space-y-2 text-sm text-ink/75">
          <li>· <b>Your first referral</b> — earn a free 1:1 color consult (or a refund if you already paid for one) <b>plus $150</b> once they book.</li>
          <li>· <b>Every referral after</b> — a flat <b>$150 finder's fee</b> the moment they book a consult or job with us.</li>
          <li>· You'll see each referral's status here so you always know where your reward stands.</li>
        </ul>
      </div>
      {items === null ? <SkeletonBlock /> : items.length === 0 ? (
        <Empty label="No referrals yet." helper="Send a friend our way and start earning as soon as they book." action={<Link to="/refer" className="text-gold underline underline-offset-4">Send your first referral</Link>} />
      ) : (
        <div className="space-y-3" data-testid="my-referrals-list">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-black/10 p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`referral-row-${r.id}`}>
              <div className="min-w-0">
                <p className="font-serif text-lg">{r.friend_name}</p>
                <p className="text-xs text-ink/60 mt-0.5">{[r.friend_city, r.friend_email, r.friend_phone].filter(Boolean).join(" · ")}</p>
                {r.project_notes && <p className="text-xs text-ink/70 mt-2 max-w-xl">{r.project_notes}</p>}
                <p className="text-[10px] text-ink/50 mt-2">Sent {new Date(r.created_at).toLocaleDateString()}</p>
              </div>
              <div className="text-right">
                <span className="inline-block text-[11px] uppercase tracking-widest px-3 py-1 bg-ivory border border-black/10">{REFERRAL_STATUS_LABEL[r.status] || r.status}</span>
                <p className="text-[10px] text-ink/50 mt-2">{r.referrer_referral_index === 1 ? "First referral — free consult + $150" : "$150 finder's fee"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------------
function Header({ eyebrow, title, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="eyebrow text-gold">{eyebrow}</p>
        <h1 className="font-serif text-3xl md:text-4xl mt-2 text-ink">{title}</h1>
      </div>
      {action}
    </div>
  );
}
function Empty({ label, helper, action }) {
  return (
    <div className="bg-white border border-dashed border-black/15 p-10 text-center" data-testid="portal-empty-state">
      <p className="font-serif text-xl">{label}</p>
      <p className="text-sm text-ink/60 mt-2 max-w-md mx-auto">{helper}</p>
      {action && <div className="mt-4 text-sm">{action}</div>}
    </div>
  );
}
function Input({ label, value, onChange, type = "text", required, minLength, testid }) {
  return (
    <label className="block">
      <p className="overline text-[10px] text-ink/60 mb-1">{label}{required && " *"}</p>
      <input required={required} minLength={minLength} type={type} value={value} onChange={(e)=>onChange(e.target.value)} data-testid={testid}
        className="w-full border border-black/15 focus:border-gold px-3 py-2 text-sm bg-white outline-none" />
    </label>
  );
}
function SkeletonBlock() {
  return <div className="animate-pulse bg-white border border-black/10 h-40" data-testid="portal-skeleton" />;
}

// Email quiz result modal for guests (used from quiz pages)
export function EmailQuizModal({ open, onClose, kind, results }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/quiz-results/email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name, kind, results, marketing_consent: consent }),
      });
      setSent(true); toast.success("Sent — check your inbox.");
    } catch (e2) { toast.error("Could not send"); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 bg-ink/60 z-[100] flex items-center justify-center p-4" data-testid="email-quiz-modal">
      <div className="bg-ivory max-w-md w-full p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-ink/60 hover:text-ink" data-testid="email-modal-close"><X size={18}/></button>
        {sent ? (
          <div className="text-center py-6" data-testid="email-modal-sent">
            <Mail className="text-gold mx-auto" size={28} />
            <p className="font-serif text-2xl mt-4">Sent to {email}</p>
            <p className="mt-2 text-sm text-ink/70">Create a free account to save every quiz, palette and home in one portal.</p>
            <Link to="/signup" className="mt-6 inline-block bg-ink text-white overline px-6 py-3 hover:bg-gold" onClick={onClose}>Create account</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="email-modal-form">
            <p className="eyebrow text-gold">Email my results</p>
            <p className="font-serif text-2xl">Send this to your inbox</p>
            <Input label="Your name" value={name} onChange={setName} testid="email-modal-name" />
            <Input label="Email" required type="email" value={email} onChange={setEmail} testid="email-modal-email" />
            <label className="flex items-start gap-2 text-xs text-ink/70">
              <input type="checkbox" checked={consent} onChange={(e)=>setConsent(e.target.checked)} data-testid="email-modal-consent" className="mt-1" />
              <span>Send me color inspiration & guides. Unsubscribe anytime.</span>
            </label>
            <Button type="submit" disabled={sending} className="w-full rounded-none bg-gold text-white hover:bg-gold-dark py-5 overline" data-testid="email-modal-submit">{sending ? "Sending…" : "Email my results"}</Button>
            <p className="text-[10px] text-ink/50 text-center">Prefer to save inside your portal? <Link to="/signup" className="text-gold underline">Create an account</Link>.</p>
          </form>
        )}
      </div>
    </div>
  );
}
